/**
 * resolveAssignedStaffRoleFlags — pick the EXACT role definition a staff
 * member is assigned to, by their assigned `staff_role_id`, not by role name.
 *
 * REQUIREMENTS SUMMARY (Issue #1058):
 * UserRoleContext reads the LIVE per-org `can_manage_athletes` flag for the
 * active staff role so the athlete-profile edit button shows/hides in
 * agreement with what RLS allows (issue #978). It used to find the staff row
 * by matching the joined `org_staff_roles.role_name` against the active role
 * string. If a gym has two role definitions that share the SAME `role_name`
 * but carry DIFFERENT capability flags (production has an `admin` pair: one
 * def with can_manage_athletes=false, 26 defs with it true), matching by name
 * could read the WRONG definition's flag and show/hide the edit button
 * incorrectly.
 *
 * The authoritative write gate is the RLS helper
 * `fga_staff_can_manage_athlete`, which keys off
 * `organization_staff.staff_role_id → org_staff_roles.id`. This helper makes
 * the cosmetic UI read use the SAME key: it selects the staff row whose
 * assigned `staff_role_id` matches the resolved active role definition's id,
 * so the flag comes from the exact definition the staff member is assigned —
 * never a different definition that merely shares the name.
 *
 * This is COSMETIC only (RLS remains the real gate); extracting it here keeps
 * the row-selection logic pure and unit-testable, separate from the React
 * context that consumes it.
 */

// A single staff row as returned by the UserRoleContext join select
// (`organization_staff` with a joined `org_staff_roles`). supabase-js may ship
// the joined relation as a single object or as a one-element array depending on
// the cardinality hint, so both shapes are tolerated here.
export interface StaffRoleJoin {
  id?: string | null;
  role_name?: string | null;
  can_manage_athletes?: boolean | null;
}

export interface StaffRowWithRoleJoin {
  // The role definition the staff member is assigned to. This is the same key
  // RLS uses (`organization_staff.staff_role_id → org_staff_roles.id`).
  staff_role_id?: string | null;
  org_staff_roles?: StaffRoleJoin | StaffRoleJoin[] | null;
}

/** Normalize the joined `org_staff_roles` (object | array | null) to a single def or null. */
function normalizeRoleJoin(
  joined: StaffRoleJoin | StaffRoleJoin[] | null | undefined
): StaffRoleJoin | undefined {
  if (Array.isArray(joined)) return joined[0];
  return joined ?? undefined;
}

/**
 * Resolve the active role NAME to the assigned role-definition id.
 *
 * The active role is chosen as a `role_name` (from the deduplicated list of the
 * staff member's roles). To disambiguate a name shared by multiple definitions,
 * we resolve it to a concrete id by reading the `staff_role_id` of the staff row
 * whose assigned definition carries that name — that id is exactly what RLS
 * keys on.
 *
 * `staffData` is already scoped to a single auth user + org, so any same-named
 * rows here are this user's own assignments. In the rare case the active name
 * maps to MORE THAN ONE of this user's assigned definitions, we prefer the one
 * that GRANTS `can_manage_athletes`. That keeps the resolved id (and therefore
 * the downstream flag read) in agreement with the RLS helper
 * `fga_staff_can_manage_athlete`, which returns TRUE if ANY of the staff
 * member's assigned definitions grants the capability.
 *
 * Returns `null` when no row matches (e.g. role not resolved yet).
 */
export function resolveActiveStaffRoleId<T extends StaffRowWithRoleJoin>(
  staffData: T[] | null | undefined,
  activeRoleName: string | null | undefined
): string | null {
  if (!staffData || !activeRoleName) return null;

  // The assigned definition's own name is authoritative: the joined def on a row
  // is the def its `staff_role_id` points to, so its `role_name` is the name for
  // that assignment.
  const matches = staffData.filter(
    (row) => normalizeRoleJoin(row.org_staff_roles)?.role_name === activeRoleName
  );
  if (matches.length === 0) return null;

  // Prefer a capability-granting assignment so the UI agrees with RLS's
  // EXISTS/OR semantics when a name maps to several of this user's defs.
  const granting = matches.find(
    (row) => normalizeRoleJoin(row.org_staff_roles)?.can_manage_athletes === true
  );
  const chosen = granting ?? matches[0];

  return chosen.staff_role_id ?? normalizeRoleJoin(chosen.org_staff_roles)?.id ?? null;
}

/**
 * Return the LIVE per-org capability flags for the role definition the staff
 * member is ASSIGNED to, selecting the staff row by `staff_role_id` (the same
 * key RLS uses) — NOT by role name.
 *
 * `can_manage_athletes`:
 *   - `boolean` when the assigned definition's flag is present, so callers can
 *     override the compile-time DEFAULT_ORG_PERMISSIONS constant with the live
 *     per-org value (issue #978).
 *   - `null` when no row matches the assigned id, or the column is absent, so
 *     callers fall back to the constant.
 */
export function resolveAssignedStaffRoleFlags<T extends StaffRowWithRoleJoin>(
  staffData: T[] | null | undefined,
  activeStaffRoleId: string | null | undefined
): { canManageAthletes: boolean | null } {
  if (!staffData || !activeStaffRoleId) {
    return { canManageAthletes: null };
  }

  // Select the staff row by its assigned definition id. Matching on the row's
  // own `staff_role_id` (preferred) — falling back to the joined def's `id` —
  // guarantees we read the flag from the EXACT definition the staff member is
  // assigned to, even when another definition shares the same `role_name`.
  const assignedRow = staffData.find((row) => {
    if (row.staff_role_id === activeStaffRoleId) return true;
    const def = normalizeRoleJoin(row.org_staff_roles);
    return def?.id === activeStaffRoleId;
  });

  if (!assignedRow) {
    return { canManageAthletes: null };
  }

  const def = normalizeRoleJoin(assignedRow.org_staff_roles);
  const canManageAthletes =
    typeof def?.can_manage_athletes === 'boolean' ? def.can_manage_athletes : null;

  return { canManageAthletes };
}
